import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiTrendingUp, FiAward, FiGlobe, FiEye, FiTarget, FiInfo, FiMap, FiUsers, FiShoppingBag } = FiIcons;

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const mainMenuItems = [
    { path: '/', icon: FiHome, label: 'Dashboard', color: 'text-blue-600' },
    { path: '/marketplace', icon: FiShoppingBag, label: 'Marketplace', color: 'text-green-600' },
  ];

  const organizationMenuItems = [
    { path: '/vision', icon: FiEye, label: 'Our Vision', color: 'text-teal-600' },
    { path: '/mission', icon: FiTarget, label: 'Our Mission', color: 'text-rose-600' },
    { path: '/about', icon: FiInfo, label: 'About Us', color: 'text-amber-600' },
    { path: '/organization', icon: FiMap, label: 'Who We Are', color: 'text-cyan-600' },
  ];

  const quickStats = [
    { icon: FiTrendingUp, label: 'Progress', value: '78%', color: 'text-green-500', action: () => navigate('/profile') },
    { icon: FiAward, label: 'Achievements', value: '12', color: 'text-yellow-500', action: () => navigate('/profile') },
    { icon: FiUsers, label: 'App Users', value: '1,247', color: 'text-blue-500', action: () => navigate('/users') },
  ];

  return (
    <aside className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 bg-white dark:bg-gray-800 shadow-lg border-r border-gray-200 dark:border-gray-700 flex flex-col">
      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto hide-scrollbar">
        <div className="p-6">
          {/* Main Navigation Menu */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Main Menu
            </h3>
            <nav className="space-y-2">
              {mainMenuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 h-12 ${
                      isActive
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-l-4 border-primary-500'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-white'
                    }`}
                  >
                    <SafeIcon
                      icon={item.icon}
                      className={`w-5 h-5 ${isActive ? 'text-primary-600 dark:text-primary-400' : item.color}`}
                    />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Organization Information */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              About ILM Global
            </h3>
            <nav className="space-y-2">
              {organizationMenuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 h-12 ${
                      isActive
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-l-4 border-primary-500'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-white'
                    }`}
                  >
                    <SafeIcon
                      icon={item.icon}
                      className={`w-5 h-5 ${isActive ? 'text-primary-600 dark:text-primary-400' : item.color}`}
                    />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Quick Stats */}
          <div className="mb-4">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Quick Stats
            </h3>
            <div className="space-y-3">
              {quickStats.map((stat, index) => (
                <button
                  key={index}
                  onClick={stat.action}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg h-12 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={stat.icon} className={`w-4 h-4 ${stat.color}`} />
                    <span className="text-sm text-gray-600 dark:text-gray-300">{stat.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{stat.value}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Motivational Quote - Reduced size */}
          <div className="mb-4 p-3 bg-gradient-to-r from-primary-500 to-accent-500 rounded-lg text-white">
            <blockquote className="text-xs italic leading-relaxed">
              "The future belongs to those who believe in the beauty of their dreams."
            </blockquote>
            <cite className="text-xs opacity-75 mt-1 block">- Eleanor Roosevelt</cite>
          </div>
        </div>
      </div>

      {/* Support Section - Fixed at bottom */}
      <div className="sticky bottom-0 w-full p-6 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-auto">
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-2">Need Help?</h4>
          <p className="text-xs text-yellow-700 dark:text-yellow-200 mb-3">
            Connect with our mentors and get the support you need.
          </p>
          <button className="w-full bg-yellow-500 text-white text-xs py-2 px-3 rounded font-medium hover:bg-yellow-600 transition-colors h-8">
            Get Support
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;